import { gql, MutationOptions } from "@apollo/client"
import { useQuery, UseQueryOptions, useMutation, InjectedOptions } from "@oysterhr/core"
import {
  Engagement,
  MutationUpdateEmploymentArgs,
  UpdateEmploymentPayload,
  UpdateEmploymentInput,
} from "@oysterhr/schema"
import { ENGAGEMENT_FRAGMENT } from "./fragments"
import { SERVICE_AGREEMENT_QUERY_NAME } from "@oysterhr/monetization/src/hooks/queries/useServiceAgreement"
import { STAFF_EMPLOYMENT_PAGE_QUERY_NAME } from "~/shared/constants/employmentPage"

export const STAFF_FINANCE_TAB_QUERY_NAME = "StaffEmploymentPageFinance"

export const FINANCE_TAB_QUERY = gql`
  query ${STAFF_FINANCE_TAB_QUERY_NAME}($engagementId: ID!) {
    engagement(engagementId: $engagementId) {
      ...StaffEmploymentPageFinanceEngagementFragment
    }
  }

  ${ENGAGEMENT_FRAGMENT}
`

export const useFinance = (
  opts?: UseQueryOptions<{ engagement: Engagement }, { engagementId: string }>,
) =>
  useQuery<{ engagement: Engagement }, { engagementId: string }>(FINANCE_TAB_QUERY, {
    fetchPolicy: "cache-and-network",
    ...opts,
  })

export const UPDATE_EMPLOYMENT = gql`
  mutation UpdateStaffEmploymentPageFinance($input: UpdateEmploymentInput!) {
    updateEmployment(input: $input) {
      engagement {
        ...StaffEmploymentPageFinanceEngagementFragment
      }
      errors
    }
  }
  ${ENGAGEMENT_FRAGMENT}
`

type UpdateFinanceInternalDetailsFormArgs = {
  input: UpdateEmploymentInput
}

const extractEmploymentInput = (input: UpdateEmploymentInput) => {
  const employmentInput = {
    companyId: input.companyId,
    employmentId: input.employmentId,
    localEmployerCostMonthlyAmount: input.localEmployerCostMonthlyAmount,
    localEmployerCostMonthlyCurrencyCode: input.localEmployerCostMonthlyCurrencyCode,
    localEmployerCostOneoffAmount: input.localEmployerCostOneoffAmount,
    localEmployerCostOneoffCurrencyCode: input.localEmployerCostOneoffCurrencyCode,
    employerTaxesAndContributionsAmount: input.employerTaxesAndContributionsAmount,
    employerTaxesAndContributionsCurrencyCode: input.employerTaxesAndContributionsCurrencyCode,
  }
  return employmentInput
}

export const useUpdateFinance = (
  options?: InjectedOptions<
    { updateEmployment: UpdateEmploymentPayload },
    MutationUpdateEmploymentArgs
  >,
) => useMutation(UPDATE_EMPLOYMENT, options)

export const useUpdateFinanceInternalDetailsForm = (
  options?: InjectedOptions<
    { updateEmployment: UpdateEmploymentPayload },
    UpdateFinanceInternalDetailsFormArgs
  >,
) => {
  const [updateEmployment] = useMutation(UPDATE_EMPLOYMENT, options)
  const updateFinance = async (mutationOptions: MutationOptions) => {
    const employmentOptions = {
      ...mutationOptions,
      variables: {
        input: extractEmploymentInput(mutationOptions.variables?.input),
      },
      refetchQueries: [
        STAFF_EMPLOYMENT_PAGE_QUERY_NAME,
        STAFF_FINANCE_TAB_QUERY_NAME,
        SERVICE_AGREEMENT_QUERY_NAME,
      ],
      awaitRefetchQueries: true,
    }

    const { data: employmentData } = await updateEmployment(employmentOptions)

    return { data: employmentData }
  }

  return [updateFinance]
}
